import { motion } from "framer-motion";
import { CalendarDays, Globe, Instagram, Mail, Music2, Phone } from "lucide-react";
import { CONTACT } from "@/content";

export default function Contact({ onBook }) {
  const rows = [
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, tid: "contact-email-link" },
    { icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref, tid: "contact-phone-link" },
    { icon: Globe, label: "Website", value: CONTACT.website, href: CONTACT.websiteHref, tid: "contact-website-link" },
  ];

  return (
    <section id="contact" data-testid="contact-booking-section" className="bg-[#FBF9F1] text-[#232D27] scroll-mt-20">
      <div className="mx-auto max-w-6xl px-5 py-20 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="overline-gold mb-4">Contact &amp; Booking</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1B3B2B] leading-tight max-w-2xl">
            Let's create something <span className="italic text-[#9E7D1E]">unforgettable.</span>
          </h2>
        </motion.div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="min-w-0 rounded-3xl bg-[#1B3B2B] text-[#FBF9F1] p-8 sm:p-10 flex flex-col"
          >
            <div className="space-y-5">
              {rows.map((r) => (
                <a
                  key={r.label}
                  data-testid={r.tid}
                  href={r.href}
                  target={r.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <span className="w-11 h-11 rounded-2xl bg-[#D4AF37]/15 flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37]/30 transition-colors duration-300">
                    <r.icon size={18} className="text-[#D4AF37]" />
                  </span>
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.22em] text-[#D1DDD5]/60">
                      {r.label}
                    </span>
                    <span className="block text-base text-[#FBF9F1] group-hover:text-[#EED88F] transition-colors duration-300">
                      {r.value}
                    </span>
                  </span>
                </a>
              ))}
            </div>
            <div className="mt-8 pt-8 border-t border-[#D4AF37]/20">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D1DDD5]/60 mb-4">
                Follow the journey
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  data-testid="social-instagram-link"
                  href={CONTACT.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-2.5 text-sm text-[#EED88F] hover:bg-[#D4AF37] hover:text-[#1B3B2B] transition-[background-color,color] duration-300"
                >
                  <Instagram size={15} /> {CONTACT.instagramHandle}
                </a>
                <a
                  data-testid="social-tiktok-link"
                  href={CONTACT.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-2.5 text-sm text-[#EED88F] hover:bg-[#D4AF37] hover:text-[#1B3B2B] transition-[background-color,color] duration-300"
                >
                  <Music2 size={15} /> {CONTACT.tiktokHandle}
                </a>
              </div>
            </div>
            <button
              data-testid="contact-book-call-button"
              onClick={onBook}
              className="btn-gold mt-8 w-full"
            >
              <CalendarDays size={16} />
              Book a Discovery Call
            </button>
          </motion.div>

          <motion.div
            data-testid="inquiry-card"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="card-cream min-w-0 p-8 sm:p-10 flex flex-col justify-center"
          >
            <p className="font-serif text-2xl text-[#1B3B2B]">Media &amp; booking inquiries</p>
            <p className="text-sm text-[#3A4D41] mt-3 leading-relaxed">
              The fastest way to reach us is a direct email — press, partnerships, speaking, or
              retreat booking. We read every note ourselves.
            </p>
            <a
              data-testid="inquiry-submit-button"
              href={`mailto:${CONTACT.email}?subject=${encodeURIComponent(
                "Media & Booking Inquiry — Shawnie & Aunnie Presents..."
              )}`}
              className="btn-gold w-full mt-8"
            >
              <Mail size={16} />
              Send Inquiry
            </a>
            <p className="text-xs text-[#8A9A86] mt-4 text-center">
              Opens your mail app addressed to {CONTACT.email}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
